import axios from 'axios';
import { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const RequestBag = () => {
  const custId = sessionStorage.getItem('customer_id') || '';

  const [formData, setFormData] = useState({
    custId,
    name: '',
    lastName: '',
    phone: '',
    email: '',
    pickupAddress: '',
    borough: '',
    city: '',
    state: '',
    zip: '',
    preferredDate: '',
    pickupTime: '',
    bagCount: '',
    specialInstructions: '',
    send_via: '',
    termsAccepted: false,
  });

  const [areaStatus, setAreaStatus] = useState({ message: '', valid: false });
  const [lastCheckedZip, setLastCheckedZip] = useState('');
  const [lastCheckedBorough, setLastCheckedBorough] = useState('');
  const [loading, setLoading] = useState(false); // <-- loading state

  // Fetch customer data from backend using custId
  useEffect(() => {
    if (!custId) return;

    const fetchCustomer = async () => {
      try {
        const res = await axios.get(`/auth/get-user/${custId}`);
        const user = res.data;

        setFormData((prev) => ({
          ...prev,
          name: user.first_name || '',
          lastName: user.last_name || '',
          phone: user.phone || '',
          email: user.email || '',
          pickupAddress: user.address || '',
          borough: user.borough || '',
          city: user.city || '',
          state: user.state || '',
          zip: user.zip || '',
        }));

        setLastCheckedZip(user.zip || '');
        setLastCheckedBorough(user.borough || '');
      } catch (err) {
        console.error('Error fetching user:', err);
        toast.error('Failed to load customer details');
      }
    };

    fetchCustomer();
  }, [custId]);

  const getTodayDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const generateTimeSlots = (selectedDate) => {
    const slots = [];
    const today = getTodayDate();
    const now = new Date();

    for (let hour = 8; hour <= 23; hour++) {
      for (let minutes of [0, 15, 30, 45]) {
        const time = new Date();
        time.setHours(hour, minutes, 0, 0);

        // If today → skip past times
        if (selectedDate === today && time < now) continue;

        // Format into 12-hour with AM/PM for both value & label
        const formatted = time.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });

        slots.push({ value: formatted, label: formatted });
      }
    }
    return slots;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'borough' || name === 'zip') {
      setAreaStatus({ message: '', valid: false });
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.termsAccepted) {
      toast.error('You must accept the terms and conditions.');
      return;
    }
    try {
      setLoading(true); // start loading
      const response = await fetch('/auth/request-bag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          custId: formData.custId,
          customerName: `${formData.name} ${formData.lastName}`.trim(),
          phone: formData.phone,
          email: formData.email,
          pickupAddress: formData.pickupAddress,
          borough: formData.borough,
          preferredDate: formData.preferredDate,
          preferredTimeSlot: formData.pickupTime,
          bagCount: formData.bagCount,
          specialInstructions: formData.specialInstructions,
          send_via: formData.send_via,
          termsAccepted: formData.termsAccepted,
        }),
      });
      const result = await response.json();
      if (response.ok) {
        toast.success(result.message || 'Request submitted successfully!');
        setFormData((prev) => ({
          ...prev,
          preferredDate: '',
          pickupTime: '',
          bagCount: '',
          specialInstructions: '',
          termsAccepted: false,
        }));
      } else {
        toast.error(result.message || 'Something went wrong.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to submit request');
    } finally {
      setLoading(false); // stop loading
    }
  };

  return (
    <>
      <div className="max-w-2xl mx-auto my-8 p-6 sm:p-6 md:p-8 bg-white rounded-lg shadow-[0_0_10px_0_#b2b2b2]">
        <h1 className="text-center text-2xl font-semibold mb-4">
          <span className=" text-[#9cc026]">Request a </span> Bag
        </h1>
        <form onSubmit={handleSubmit} className="space-y-3 text-sm">
          {/* --- Account Info --- */}
          <div>
            <label className="block text-sm font-medium mb-1">Account Number</label>
            <input type="text" name="custId" value={formData.custId} readOnly required
              className="w-full p-2.5 rounded-md bg-gray-100 text-gray-500 border border-gray-300 cursor-not-allowed text-sm" />
          </div>

          {/* --- Name Fields --- */}
          <div className="md:flex md:gap-3">
            <div className="w-full md:w-1/2">
              <label className="block text-sm font-medium mb-1">First name</label>
              <input type="text" value={formData.name} readOnly
                className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
            </div>
            <div className="w-full md:w-1/2 mt-3 md:mt-0">
              <label className="block text-sm font-medium mb-1">Last name</label>
              <input type="text" value={formData.lastName} readOnly
                className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
            </div>
          </div>

          {/* --- Contact Info --- */}
          <div className="md:flex md:gap-3">
            <div className="w-full md:w-1/2">
              <label className="block text-sm font-medium mb-1">Phone</label>
              <input type="text" value={formData.phone} readOnly
                className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
            </div>
            <div className="w-full md:w-1/2 mt-3 md:mt-0">
              <label className="block text-sm font-medium mb-1">Email</label>
              <input type="email" value={formData.email} readOnly
                className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
            </div>
          </div>

          {/* --- City / Borough --- */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Pickup Address</label>
              <textarea value={formData.pickupAddress} readOnly
                className="w-full p-2.5 rounded-md bg-green-50 border text-sm h-[40px] resize-none overflow-hidden" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">City</label>
              <input type="text" name="city" value={formData.city} onChange={handleChange}
                className="w-full p-2.5 rounded-md bg-green-50 border border-gray-300 text-sm" required />
            </div>
          </div>

          {/* --- State / Zip / Availability --- */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">State</label>
              <input type="text" name="state" value={formData.state} onChange={handleChange} required
                className="p-2.5 rounded-md bg-gray-100 text-gray-500 border border-gray-300 cursor-not-allowed w-full text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Borough</label>
              <input type="text" name="borough" value={formData.borough} onChange={handleChange} required
                className="p-2.5 rounded-md bg-gray-100 text-gray-500 border border-gray-300 cursor-not-allowed w-full text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Zip</label>
              <input type="text" name="zip" value={formData.zip} onChange={handleChange} required
                className="p-2.5 rounded-md bg-gray-100 text-gray-500 border border-gray-300 cursor-not-allowed w-full text-sm" />
            </div>
          </div>

          {/* --- Number of Bags --- */}
          <div>
            <label className="block text-sm font-medium mb-1">Number of Bags Needed</label>
            <select name="bagCount" value={formData.bagCount} onChange={handleChange}
              className="w-full p-2.5 rounded-md bg-green-50 border text-sm" required >
              <option value="">Select number of bags</option>
              {[1, 2, 3, 4, 5].map((num) => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>

          {/* --- Preferred Date / Time --- */}
          <div className="md:flex md:gap-3">
            <div className="w-full md:w-1/2">
              <label className="block text-sm font-medium mb-1">Preferred Date</label>
              <input type="date" name="preferredDate" value={formData.preferredDate}
                onChange={handleChange} min={getTodayDate()} required
                className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
              />
            </div>
            <div className="w-full md:w-1/2 mt-3 md:mt-0">
              <label className="block text-sm font-medium mb-1">Pick a Time</label>
              <select name="pickupTime" value={formData.pickupTime} onChange={handleChange}
                className="w-full p-2.5 rounded-md bg-green-50 border border-gray-300 text-sm" required>
                <option value=""> -- Select a time -- </option>
                {generateTimeSlots(formData.preferredDate).map((slot, idx) => (
                  <option key={idx} value={slot.value}>{slot.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* --- Send Via --- */}
          <div>
            <label className="block text-sm font-medium mb-1">Send Bag By</label>
            <select name="send_via" value={formData.send_via} onChange={handleChange}
              className="w-full p-2.5 bg-green-50 rounded-md border border-gray-300 text-sm" required>
              <option value="">-- Select Method --</option>
              <option value="Post">Send via Post (Bag Physically Mailed)</option>
              <option value="Email">Send via Email (Barcode emailed)</option>
            </select>
          </div>

          {/* --- Special Instructions --- */}
          <div>
            <label className="block text-sm font-medium mb-1">Special Instructions</label>
            <textarea name="specialInstructions" value={formData.specialInstructions} onChange={handleChange}
              className="w-[300px] p-2.5 rounded-md bg-green-50 border text-sm h-[60px]" />
          </div>

          {/* --- Terms --- */}
          <div className="flex items-center">
            <input type="checkbox" name="termsAccepted" checked={formData.termsAccepted}
              onChange={handleChange} required className="mr-2" />
            <label className="text-sm text-gray-700">I agree to the terms and conditions</label>
          </div>

          {/* --- Submit --- */}
          <div className="flex justify-end">
            <button type="submit" disabled={loading}
              className={`px-4 py-2.5 font-semibold rounded-3xl transition 
                ${loading ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-[#54BE73] hover:bg-green-700 text-white'}`}>
              {loading ? 'Submitting...' : 'SCHEDULE YOUR REQUEST'}
            </button>
          </div>
        </form>
      </div>
      <ToastContainer />
    </>
  );
};

export default RequestBag;
